import {
  EPatientStatus,
  TRoomViewPatientStatusHistory,
  TRoomViewProvider,
  TRoomViewScheduledSurgery,
} from './RoomView.types'
import { TPatientStatusOption } from 'lib/services/restApi/organizations/organizations.types'

export const getProviderName = (
  provider: TRoomViewProvider | undefined
): string => {
  if (!provider) return ''
  return `${provider.lastName}, ${provider.firstName[0]}`
}

export const getProviderNameWithCredentials = (
  provider: TRoomViewProvider | undefined
): string => {
  if (!provider) return ''
  let credentials = ''
  if (provider.credentials) {
    credentials = provider.credentials
      .map((credential) => credential)
      .join(', ')
  }
  return `${provider.lastName} ${provider.firstName}${
    credentials ? `, ${credentials}` : ''
  }`
}

export const getSurgeryDescription = (
  surgery: TRoomViewScheduledSurgery
): string => {
  const procedureInfo = surgery.procedure.procedureInfo
    .map((info) => info.value)
    .filter((value) => value && value !== 'N/A')
    .join(', ')

  return `${surgery.procedure.description} • ${procedureInfo}`
}

export const getTimeFromDate = (date: Date | string | undefined): string => {
  if (!date) return ''
  try {
    const dateObj = date instanceof Date ? date : new Date(date)

    if (isNaN(dateObj.getTime())) {
      return ''
    }

    return dateObj.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
  } catch (error) {
    return 'Invalid time'
  }
}

export const getAgeFromDate = (date: Date | string | undefined): number => {
  if (!date) return 0
  const dateObj = date instanceof Date ? date : new Date(date)
  const today = new Date()
  const age = today.getFullYear() - dateObj.getFullYear()
  return age
}

export const getEndTimeFromDate = (
  startTime: Date | string,
  duration: number
): string => {
  const dateObj = startTime instanceof Date ? startTime : new Date(startTime)
  const endTime = new Date(dateObj.getTime() + duration * 60000)
  return getTimeFromDate(endTime)
}

export const formatDuration = (minutes: number): string => {
  if (!minutes) return ''
  if (minutes < 1) return '1m'
  if (minutes < 60) return `${Math.floor(minutes)}m`
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = Math.floor(minutes) % 60
  return `${hours}h${remainingMinutes > 0 ? ` ${remainingMinutes}m` : ''}`
}

export const getDurationBetweenDates = (
  startDate: Date | string | undefined,
  endDate: Date | string | undefined
): string => {
  if (!startDate || !endDate) return ''
  const start = startDate instanceof Date ? startDate : new Date(startDate)
  const end = endDate instanceof Date ? endDate : new Date(endDate)
  return formatDuration((end.getTime() - start.getTime()) / 60000)
}

export const getStatusColor = (
  status: EPatientStatus | undefined,
  patientStatusOptions: TPatientStatusOption[]
): string => {
  if (!status) return 'transparent'
  return (
    patientStatusOptions.find((option) => option.status === status)?.color ||
    'transparent'
  )
}

export const getStatusLabel = (
  status: EPatientStatus | undefined,
  patientStatusOptions: TPatientStatusOption[]
): string => {
  if (!status) return ''
  if (status === EPatientStatus.ASSIGN_ROOM)
    return (
      patientStatusOptions.find((option) => option.status === status)
        ?.displayText || ''
    )
  return (
    patientStatusOptions.find((option) => option.status === status)?.value || ''
  )
}

export const calculateWaitingTimeForCheckedIn = (
  patientStatusHistory: TRoomViewPatientStatusHistory[]
): string => {
  if (!patientStatusHistory || patientStatusHistory.length < 2) return ''

  const checkedInIndex = patientStatusHistory.findIndex(
    (statusItem) => statusItem.status === EPatientStatus.CHECKED_IN
  )

  if (checkedInIndex === -1 || checkedInIndex === 0) {
    return ''
  }

  const previousStatus = patientStatusHistory[checkedInIndex - 1]
  const checkedInStatus = patientStatusHistory[checkedInIndex]

  return getDurationBetweenDates(
    checkedInStatus.info.createdAt,
    previousStatus.info.createdAt
  )
}

export const naturalSort = (a: string, b: string): number => {
  const aParts = a.split(/(\d+)/).filter(Boolean)
  const bParts = b.split(/(\d+)/).filter(Boolean)

  for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
    const aPart = aParts[i] || ''
    const bPart = bParts[i] || ''

    const aIsNumber = /^\d+$/.test(aPart)
    const bIsNumber = /^\d+$/.test(bPart)

    if (aIsNumber && bIsNumber) {
      const aNum = parseInt(aPart, 10)
      const bNum = parseInt(bPart, 10)
      if (aNum !== bNum) {
        return aNum - bNum
      }
    } else {
      const result = aPart.localeCompare(bPart)
      if (result !== 0) {
        return result
      }
    }
  }

  return 0
}

export const calculateAverageWaitingTime = (
  scheduledSurgeries: TRoomViewScheduledSurgery[]
): string => {
  if (!scheduledSurgeries || scheduledSurgeries?.length === 0) {
    return 'N/A'
  }

  const waitingTimes: number[] = []

  scheduledSurgeries.forEach((surgery) => {
    const { patientStatusHistory } = surgery

    if (patientStatusHistory?.length > 0) {
      const now = new Date()
      const period =
        now.getTime() -
        new Date(patientStatusHistory?.[0]?.info?.createdAt).getTime()

      if (period) {
        waitingTimes.push(period)
      }
    }
  })

  if (waitingTimes.length === 0) {
    return 'N/A'
  }

  const averageMinutes =
    waitingTimes.reduce((sum, time) => sum + time, 0) / waitingTimes.length

  return formatDuration(averageMinutes / 60000)
}

export const calculateSurgeryExpectedEndTime = (
  surgery: TRoomViewScheduledSurgery
): string => {
  const startTime = surgery.patientStatusHistory[0]?.info?.createdAt
  const duration = surgery.procedure.duration

  if (!startTime || !duration) return 'N/A'

  const startDate = new Date(startTime)
  const expectedEndTime = new Date(startDate.getTime() + duration * 60000) // duration in minutes

  // Format as time (e.g., "2:30 PM")
  return expectedEndTime.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

export const formatUSPhoneNumberForDisplay = (phone: string): string => {
  const cleaned = phone.replace(/^\+1/, '')

  if (cleaned.length !== 10) {
    return phone
  }

  const areaCode = cleaned.slice(0, 3)
  const centralOfficeCode = cleaned.slice(3, 6)
  const lineNumber = cleaned.slice(6)

  return `(${areaCode}) ${centralOfficeCode}-${lineNumber}`
}
